import React, { FC, PropsWithChildren, useMemo, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useGetOrdersByIds } from "@/services/Orders";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";

import styles from "./styles.module.css";
import Text from "@/components/atoms/Text";
import Group from "@/components/atoms/Group";
import CTA from "@/components/atoms/CTA";
import { Order, OrderAmount, Task } from "@/types";
import { Grid } from "@mantine/core";
import { Controller, useForm, UseFormSetValue } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  MultipleFinalAmountFormInput,
  MultipleFinalAmountValidationSchema,
} from "@/components/pages/Tasks/validation/finalAmountValidation";
import { postFinalAmounts } from "@/services/Orders";
import { useAuth } from "@/contexts/AuthContext";
import DrawerAccordion from "@/components/atoms/accordions/DrawerAccordion";
import DrawerTabs, { DrawerTab } from "@/components/atoms/DrawerTabs";
import Divider from "@/components/atoms/Divider";
import Badge from "@/components/atoms/Badge";
import NumberInput from "@/components/atoms/NumberInput";
import OrderProductDetails from "@/components/molecules/DrawerDetails/OrderProductDetails";
import DetailsRow from "@/components/molecules/DrawerDetails/DetailsRow";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { notifications } from "@mantine/notifications";

interface Props extends PropsWithChildren {
  task: Task;
  onClose: () => void;
}

const FinalAmountMultipleTask: FC<Props> = (props) => {
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const initialAccordionSet = useRef(false);

  const { task, onClose } = props;
  const { t } = useTranslation();
  const { currentCompany, user } = useAuth();
  const { orders } = useGetOrdersByIds(task?.orderIds);

  const formId = "final-amount-form";

  const initialRecord = useMemo(() => {
    const defaults: Record<string, number> = {};

    if (task?.orderIds && (!orders || orders.length === 0)) {
      task.orderIds.forEach((id) => {
        defaults[id.toString()] = 0;
      });
      return defaults;
    }

    if (orders && orders.length > 0) {
      return orders.reduce((acc, order) => {
        acc[order.id.toString()] = order.amount ?? 0;
        return acc;
      }, defaults);
    }

    return defaults;
  }, [orders, task?.orderIds]);

  const {
    handleSubmit,
    control,
    formState: { isValid },
    setError,
    setValue,
  } = useForm<MultipleFinalAmountFormInput>({
    resolver: zodResolver(MultipleFinalAmountValidationSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    values: initialRecord,
  });

  useEffect(() => {
    if (!initialAccordionSet.current && orders && orders.length > 0) {
      setOpenAccordion(orders[0].id.toString());
      initialAccordionSet.current = true;
    }
  }, [orders, openAccordion]);

  const { mutateAsync: mutateMultipleFinalAmounts, isPending } = useMutation({
    mutationFn: async ({ orderAmounts, taskId }: { orderAmounts: OrderAmount[]; taskId: number }) => {
      await postFinalAmounts(orderAmounts, taskId);
    },
    onSuccess: async () => {
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:orders.finalAmountSet"))
      );
      await queryClient.invalidateQueries({
        queryKey: ["getOpenTasks", currentCompany?.companyId],
      });
    },
    onError: async () => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:orders.finalAmountSetError"))
      );
      await queryClient.invalidateQueries({
        queryKey: ["getOpenTasks", currentCompany?.companyId],
      });
    },
  });

  const onSubmit = async (data: MultipleFinalAmountFormInput) => {
    const orderAmounts: any = {
      orderamounts: [],
    };
    let hasDeviationError = false;

    for (const [orderId, amount] of Object.entries(data)) {
      if (isNaN(amount)) continue;

      const originalOrder = orders.find((order) => order.id === parseInt(orderId));
      if (!originalOrder) continue;

      const originalAmount = originalOrder.amount;
      const amountDeviation = originalOrder.amountDeviation || 0;

      const deviation: number = (originalAmount / 100) * amountDeviation;
      const lowEnd: number = originalAmount - deviation;
      const highEnd: number = originalAmount + deviation;

      if (amount > highEnd || amount < lowEnd) {
        hasDeviationError = true;

        setError(`${orderId}`, {
          type: "manual",
          message:
            t("tasks:legalFinalAmount") +
            lowEnd +
            originalOrder.unit?.unit +
            " - " +
            highEnd +
            originalOrder.unit?.unit,
        });
        setOpenAccordion(orderId);
        continue;
      }

      orderAmounts.orderamounts.push({
        orderId: parseInt(orderId),
        amount: amount,
      });
    }

    if (hasDeviationError) {
      return;
    }
    await mutateMultipleFinalAmounts({ orderAmounts, taskId: task.id });
    onClose();
  };

  return (
    <DrawerTabs defaultValue="orders" className="full flex col between h-100 py-10">
      <Group gap="20px" align="center" className={styles.taskHeader}>
        <SVGAsset name={orders[0]?.orderType.type === "BUY" ? "purchaseIndicator" : "saleIndicator"} />
        <Text size="md" display="technical" tt="uppercase" strong={true}>
          {t("enumerables:taskTypes." + task?.type)}
        </Text>
      </Group>

      {/* Form with ID to connect to external submit button */}
      <DrawerTabs.List>
        <DrawerTabs.Tab value="orders">
          <DrawerTab svgName="hamburger" label={t("tasks:orders")} />
        </DrawerTabs.Tab>
      </DrawerTabs.List>
      <DrawerTabs.Panel value="orders" className={styles.tabPanel}>
        <form id={formId} onSubmit={handleSubmit(onSubmit)} className={styles.tabPanel}>
          <DrawerAccordion unstyled={true} value={openAccordion} onChange={setOpenAccordion}>
            {orders &&
              orders.map((order) => {
                const productLabel = `${order.salesItem.nameTranslated?.[user?.language.id as number] ?? order.salesItem.salesItem} | ${order.properties[0] ? order.properties[0].value : ""}`;
                return (
                  <DrawerAccordion.Item key={order.id} value={order.id.toString()}>
                    <DrawerAccordion.Control>
                      <Text size="sm" display="technical" tt="uppercase" strong={true}>
                        {order.id} | {productLabel}
                      </Text>
                    </DrawerAccordion.Control>
                    <DrawerAccordion.Panel>
                      <Grid>
                        <OrderProductDetails
                          defaultProductName={`${order.salesItem.nameTranslated?.[user?.language.id as number] ?? order.salesItem.salesItem} - ${order.properties[0] ? order.properties[0].value : ""}`}
                          product={order.companySpecificProductDto}
                        />
                        <DetailsRow
                          label={"tasks:amountOrdered"}
                          value={`${order.amount} ${order.unit?.unit}`}
                          isLoading={false}
                        />
                        <Grid.Col span={12}>
                          <Divider />
                        </Grid.Col>
                        <FinalAmount order={order} control={control} setValue={setValue} />
                      </Grid>
                    </DrawerAccordion.Panel>
                  </DrawerAccordion.Item>
                );
              })}
          </DrawerAccordion>
        </form>
      </DrawerTabs.Panel>
      <Group className={styles.tabActionGroup} p={15} gap={16} align="center" justify="flex-end">
        <CTA
          data-testid="final-amount-multiple-submit-btn"
          intent="primary"
          size="sm"
          fullWidth={false}
          type="submit"
          form={formId}
          disabled={!isValid || isPending}
          loading={isPending}
        >
          {t("actions:approve")}
        </CTA>
        <CTA intent="secondary" size="sm" fullWidth={false} type="button" onClick={onClose} disabled={isPending}>
          {t("actions:shutdown")}
        </CTA>
      </Group>
    </DrawerTabs>
  );
};

export default FinalAmountMultipleTask;

interface FinalAmountProps {
  order: Order;
  control: any;
  setValue: UseFormSetValue<MultipleFinalAmountFormInput>;
}

const FinalAmount: React.FC<FinalAmountProps> = ({ order, control }) => {
  const [loggedAmount, setLoggedAmount] = useState<null | number>(0);
  const { t } = useTranslation();

  return (
    <>
      <DetailsRow
        label={"tasks:loggedAmount"}
        value={loggedAmount && `${loggedAmount} ${order.unit?.unit}`}
        isLoading={false}
      />
      <Grid.Col span={3}>
        <Text size="sm" c="dimmed">
          {t("tasks:finalAmount")}
        </Text>
      </Grid.Col>
      <Grid.Col span={9}>
        <Group wrap={"nowrap"}>
          <Controller
            control={control}
            name={order.id.toString()}
            render={({ field, fieldState }) => {
              const handleLog = () => {
                field.onChange(field.value);
                setLoggedAmount(field.value);
                field.onBlur();
              };

              return (
                <Group>
                  <span
                    style={{
                      position: "relative",
                      display: "inline-block",
                      overflow: "hidden",
                    }}
                  >
                    <NumberInput
                      data-testid={`final-amount-input-${order.id}`}
                      hideControls
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={
                        fieldState.error
                          ? fieldState.error.type === "manual"
                            ? fieldState.error.message
                            : t(fieldState.error.message as string)
                          : undefined
                      }
                    />
                    <Badge
                      size={"sm"}
                      h={35}
                      radius={0}
                      color={"primary"}
                      pos={"absolute"}
                      top={0}
                      right={0}
                      bottom={0}
                    >
                      {order.unit?.unit}
                    </Badge>
                  </span>

                  <CTA
                    data-testid={`final-amount-log-btn-${order.id}`}
                    size="sm"
                    intent="secondary"
                    onClick={handleLog}
                  >
                    {t("tasks:log")}
                  </CTA>
                </Group>
              );
            }}
          />
        </Group>
      </Grid.Col>
    </>
  );
};
